import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { loadConsent, readConsent } from '@/utils/cookieConsentStorage';
import { isWidget } from '@/utils/isWidget';

interface CookieConsentSingletonContextType {
  registerInstance: (id: string) => boolean;
  unregisterInstance: (id: string) => void;
  isActiveInstance: (id: string) => boolean;
  pendingResponse: boolean;
  setPendingResponse: (value: boolean) => void;
}

const CookieConsentSingletonContext = createContext<
  CookieConsentSingletonContextType | undefined
>(undefined);

export interface CookieConsentSingletonProviderProps {
  children: React.ReactNode;
}

export function CookieConsentSingletonProvider({
  children,
}: CookieConsentSingletonProviderProps) {
  const [instanceStack, setInstanceStack] = useState<string[]>([]);
  const [pendingResponse, setPendingResponse] = useState<boolean>(() => {
    if (typeof window === 'undefined' || isWidget) return false;
    return !readConsent();
  });

  useEffect(() => {
    if (!isWidget) return;
    let cancelled = false;
    void loadConsent().then((consent) => {
      if (!cancelled) setPendingResponse(consent === null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const registerInstance = useCallback((id: string): boolean => {
    setInstanceStack((current) => [...current, id]);
    return true;
  }, []);

  const unregisterInstance = useCallback((id: string) => {
    setInstanceStack((current) =>
      current.filter((instanceId) => instanceId !== id),
    );
  }, []);

  const isActiveInstance = useCallback(
    (id: string): boolean => {
      return (
        instanceStack.length > 0 &&
        instanceStack[instanceStack.length - 1] === id
      );
    },
    [instanceStack],
  );

  const value: CookieConsentSingletonContextType = {
    registerInstance,
    unregisterInstance,
    isActiveInstance,
    pendingResponse,
    setPendingResponse,
  };

  return (
    <CookieConsentSingletonContext.Provider value={value}>
      {children}
    </CookieConsentSingletonContext.Provider>
  );
}

export function useCookieConsentSingleton(): CookieConsentSingletonContextType {
  const context = useContext(CookieConsentSingletonContext);
  if (context === undefined) {
    throw new Error(
      'useCookieConsentSingleton must be used within a CookieConsentSingletonProvider',
    );
  }
  return context;
}
